import React, { KeyboardEvent, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';
import { MarginProps } from '../../../../../theme/styledProps';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { Select } from '../../../../../CommonComponents/OPL/Select/Select/Select';
import { HelperText } from '../../../../../CommonComponents/OPL/HelperText/HelperText';
import { EmailsOptions, fieldName, TOOLTIP_TEXT } from './SupportEmails.meta';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { Tooltip } from '../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { FormValues } from '../../FirmSettings.meta';

interface Props extends MarginProps {
  formBag: UseFormReturn<FormValues>;
}

export const SupportEmails: React.FC<Props> = ({ formBag, ...rest }) => {
  const [inputValue, setInputValue] = useState('');
  const [innerError, setInnerError] = useState<string | null>(null);

  const emails = formBag.watch(fieldName);

  const addEmail = () => {
    // Do not add the same email more than once
    if (emails?.find(email => email === inputValue)) {
      setInnerError('Choose a different email');

      setTimeout(() => setInnerError(null), 2000);
    } else {
      formBag.setValue(fieldName, [...(emails || []), inputValue], { shouldDirty: true });

      setInputValue('');
    }
  };

  const handleChange = (opts: EmailsOptions) => {
    formBag.setValue(
      fieldName,
      opts.map(({ value: email }) => email),
      { shouldDirty: true },
    );
  };

  const handleInputChange = (newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleKeyDown = (evt: KeyboardEvent<HTMLElement>) => {
    if (!inputValue) {
      return;
    }

    if (['Enter', 'Tab', ' ', ','].includes(evt.key)) {
      evt.stopPropagation();
      evt.preventDefault();

      addEmail();
    }
  };

  const handleBlur = () => {
    if (!inputValue) {
      return;
    }

    addEmail();
  };

  return (
    <Box as="section" {...rest}>
      <Box display="flex" alignItems="center" mb="4">
        <Text variant="formLabel">
          Email Addresses to send notifications when documents are uploaded to the Document Vault
        </Text>

        <Tooltip content={TOOLTIP_TEXT}>
          <Icon className="far fa-info-circle" color="neutral.500" fontSize="small" ml="4" />
        </Tooltip>
      </Box>

      <Controller
        control={formBag.control}
        name={fieldName}
        render={({ field: { value } }) => {
          return (
            <>
              <Select
                isMulti
                isClearable={false}
                value={(value || []).map(email => ({
                  value: email,
                  label: email,
                  isInvalid: !yup
                    .string()
                    .email()
                    .isValidSync(email),
                }))}
                inputValue={inputValue}
                components={{
                  DropdownIndicator: null,
                }}
                menuIsOpen={false}
                onChange={opts => {
                  handleChange((opts as unknown) as EmailsOptions);
                }}
                onInputChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                placeholder="Type an email and press enter..."
                aria-label="Select Email Address"
              />

              {innerError && (
                <HelperText variant="error" mt="4">
                  {innerError}
                </HelperText>
              )}

              {formBag.formState.errors[fieldName] && (
                <HelperText variant="error" mt="4">
                  Please enter only valid email addresses
                </HelperText>
              )}
            </>
          );
        }}
      />
    </Box>
  );
};
